import React, { useEffect, useRef, useState } from "react";
import { useInView, animate } from "framer-motion";

/**
 * AnimatedCounter — counts up from 0 (or `from`) to `value` when scrolled into view.
 *
 * Props:
 * - value: final numeric value
 * - duration: seconds (default 1.8)
 * - from: starting value (default 0)
 * - prefix / suffix: strings placed before/after the number
 * - decimals: decimal digits (default 0)
 * - format: optional custom formatter (v: number) => string, overrides decimals+locale
 * - once: only animate first time (default true)
 * - className: forwarded to root span
 */
export const AnimatedCounter = ({
  value,
  from = 0,
  duration = 1.8,
  prefix = "",
  suffix = "",
  decimals = 0,
  format,
  once = true,
  className = "",
  ...rest
}) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once, margin: "-40px" });
  const [display, setDisplay] = useState(() =>
    formatNumber(from, decimals, format)
  );

  useEffect(() => {
    if (!inView) return undefined;
    const controls = animate(from, value, {
      duration,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (latest) => setDisplay(formatNumber(latest, decimals, format)),
    });
    return () => controls.stop();
  }, [inView, value, from, duration, decimals, format]);

  return (
    <span ref={ref} className={className} {...rest}>
      {prefix}
      {display}
      {suffix}
    </span>
  );
};

function formatNumber(v, decimals, format) {
  if (typeof format === "function") return format(v);
  const rounded = decimals > 0 ? Number(v).toFixed(decimals) : Math.round(v).toString();
  // brazilian format: 1.234,56 — for our small ints this is fine as is
  if (decimals > 0) return rounded.replace(".", ",");
  return rounded;
}

export default AnimatedCounter;
